import React, { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { DataGrid } from "@mui/x-data-grid";
import { Alert, Box, Button, Snackbar, Typography } from "@mui/material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";

const ProceedingHeaders = () => {
  const apiBase = `${process.env.REACT_APP_API_BASE_URL}/api/fee-proceedings-headers`;
  const user = JSON.parse(sessionStorage.getItem("user") || "null");
  const clientId = user?.client_id;
  const collegeId = user?.college_id;
  const api = `${apiBase}/${clientId}/${collegeId}`;
  const [rows, setRows] = useState([]);
  const [selectionModel, setSelectionModel] = useState([]);
  const [busy, setBusy] = useState(false);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  const notify = (message, severity = "success") => {
    setSnackbar({ open: true, message, severity });
  };

  const loadPendingApprovals = useCallback(async () => {
    setBusy(true);

    try {
      const { data } = await axios.get(`${api}/pending-approvals`);

      const result = Array.isArray(data) ? data : data.data || [];

      setRows(
        result.map((row) => ({
          ...row,
          id: String(row.proceedings_header_id),
        }))
      );

      setSelectionModel([]);
    } catch (error) {
      notify(
        error.response?.data?.message || "Unable to load pending proceeding headers.",
        "error"
      );
    } finally {
      setBusy(false);
    }
  }, [api, clientId, collegeId]);

  useEffect(() => {
    loadPendingApprovals();
  }, [loadPendingApprovals]);

  const approveOne = async (row) => {
    if (!window.confirm(`Approve proceeding header ${row.proceedingno}?`)) {
      return;
    }

    setBusy(true);

    try {
      const { data } = await axios.post(`${api}/approve`, {
        id: row.proceedings_header_id,
        proceeding_no: row.proceedingno,
      });

      notify(data.message || "Proceeding header approved.");
      await loadPendingApprovals();
    } catch (error) {
      notify(error.response?.data?.message || "Approval failed.", "error");
    } finally {
      setBusy(false);
    }
  };

  const approveSelected = async () => {
    const selectedRows = rows.filter((row) => selectionModel.includes(row.id));

    if (!selectedRows.length) {
      notify("Please select at least one header.", "warning");
      return;
    }

    if (!window.confirm(`Approve all ${selectedRows.length} selected proceeding headers?`)) {
      return;
    }

    setBusy(true);

    try {
      const { data } = await axios.post(`${api}/approve-bulk`, {
        rows: selectedRows.map((row) => ({
          id: row.proceedings_header_id,
          proceeding_no: row.proceedingno,
        })),
      });

      notify(data.message || "Selected headers approved.");
      await loadPendingApprovals();
    } catch (error) {
      notify(
        error.response?.data?.message || "Bulk approval failed. No records were approved.",
        "error"
      );
    } finally {
      setBusy(false);
    }
  };

  const columns = useMemo(
    () => [
      {
        field: "proceedings_header_id",
        headerName: "Header ID",
        width: 100,
      },
      {
        field: "academic_year",
        headerName: "Academic Year",
        width: 120,
      },
      {
        field: "proceedingno",
        headerName: "Proceeding No",
        minWidth: 155,
        flex: 1,
      },
      {
        field: "proceedingdate",
        headerName: "Proceeding Date",
        width: 130,
        valueFormatter: (p) => (p.value ? new Date(p.value).toLocaleDateString("en-GB") : "-"),
      },
      {
        field: "caste",
        headerName: "Caste",
        width: 145,
      },
      {
        field: "quarterno",
        headerName: "Quarter",
        width: 85,
      },
      {
        field: "releasecount",
        headerName: "Release Count",
        width: 110,
      },
      {
        field: "amount",
        headerName: "Amount",
        width: 130,
        valueFormatter: (p) =>
          Number(p.value || 0).toLocaleString("en-IN", {
            style: "currency",
            currency: "INR",
            minimumFractionDigits: 2,
          }),
      },
      {
        field: "bankname",
        headerName: "Bank",
        width: 120,
      },
      {
        field: "branchname",
        headerName: "Branch",
        width: 120,
      },
      {
        field: "releaseddate",
        headerName: "Released Date",
        width: 135,
        valueFormatter: (p) => (p.value ? new Date(p.value).toLocaleDateString("en-GB") : "-"),
      },
      {
        field: "actions",
        headerName: "Action",
        width: 140,
        sortable: false,
        renderCell: (p) => (
          <Button
            size="small"
            variant="contained"
            startIcon={<CheckCircleIcon />}
            disabled={busy || !p.row.releaseddate}
            onClick={() => approveOne(p.row)}
            sx={{
              backgroundColor: "#1976d2",
              color: "#fff",
              fontWeight: "bold",
              "&:hover": { backgroundColor: "#1565c0" },
            }}
          >
            Approve
          </Button>
        ),
      },
    ],
    [busy, selectionModel]
  );

  return (
    <Box p={2}>
      <Typography variant="h5" fontWeight="bold" mb={2}>
        APPROVE HEADERS OF PROCEEDINGS
      </Typography>

      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        gap={2}
        flexWrap="wrap"
        mb={2}
      >
        <Typography variant="subtitle1" fontWeight="bold">
          Approval Pending List of Proceeding Headers
        </Typography>

        <Button
          variant="contained"
          startIcon={<CheckCircleIcon />}
          disabled={busy || selectionModel.length === 0}
          onClick={approveSelected}
          sx={{
            backgroundColor: "#2e7d32",
            color: "#fff",
            fontWeight: "bold",
            "&:hover": { backgroundColor: "#1b5e20" },
          }}
        >
          Approve Selected ({selectionModel.length})
        </Button>
      </Box>

      <Box sx={{ width: "100%", overflowX: "auto" }}>
        <Box sx={{ minWidth: 1000 }}>
          <DataGrid
            autoHeight
            rows={rows}
            columns={columns}
            loading={busy}
            checkboxSelection
            disableSelectionOnClick
            selectionModel={selectionModel}
            onSelectionModelChange={(newSelection) => {
              setSelectionModel(newSelection.map(String));
            }}
            isRowSelectable={(params) => Boolean(params.row.releaseddate)}
            pageSize={10}
            rowsPerPageOptions={[10, 25, 50]}
            getRowId={(row) => row.id}
            sx={{
              "& .MuiDataGrid-columnHeaders": {
                backgroundColor: "#e3f2fd",
                fontWeight: "bold",
              },
            }}
          />
        </Box>
      </Box>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Alert
          severity={snackbar.severity}
          variant="filled"
          onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default ProceedingHeaders;
